import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';

import { getOfficerLabel } from '@/utils/legacy-utils';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
import { getOfficerPluralLabel } from '@/utils/auth';
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface Officer {
    uuid: string;
    id: any;
    officerId: any;
    name: string;
    isPrimary: boolean;
    dispatched: Date | null;
    enroute: Date | null;
    onScene: Date | null;
    cleared: Date | null;
    [key: string]: any;
}

interface Person {
    id: string | number;
    name: string;
    [key: string]: any;
}

interface EditingField {
    uuid: string;
    field: string;
}

interface DeleteConfirmState {
    open: boolean;
    uuid: string | null;
    name: string;
}

interface TimeFieldInfo {
    label: string;
    field: string;
}

export interface CaseReportOfficersAssignedProps {
    officers: Officer[];
    people?: Person[];
    title?: string;
    isReadOnly?: boolean;
    onChange?: (officers: Officer[]) => void;
}

export function CaseReportOfficersAssigned({
    officers,
    people = [],
    title,
    isReadOnly = false,
    onChange,
}: CaseReportOfficersAssignedProps) {
    const { theme } = useTheme();

    // State
    const [editingField, setEditingField] = useState<EditingField | null>(null);
    const [editValue, setEditValue] = useState<any>('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerOpenDesktop, setDatePickerOpenDesktop] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isTabbing, setIsTabbing] = useState(false);
    const [justSelected, setJustSelected] = useState(false);
    const [savedEditingFieldForTab, setSavedEditingFieldForTab] = useState<EditingField | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
        open: false,
        uuid: null,
        name: '',
    });

    // Refs
    const rootRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const editingFieldRef = useRef(editingField);
    const editValueRef = useRef(editValue);
    const isTabbingRef = useRef(isTabbing);
    const justSelectedRef = useRef(justSelected);
    const officersRef = useRef(officers);
    const savedEditingFieldForTabRef = useRef(savedEditingFieldForTab);
    const pendingEditActionRef = useRef<string | null>(null);
    const shouldScrollToLastRef = useRef(false);

    // Keep refs in sync
    useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
    useEffect(() => { editValueRef.current = editValue; }, [editValue]);
    useEffect(() => { isTabbingRef.current = isTabbing; }, [isTabbing]);
    useEffect(() => { justSelectedRef.current = justSelected; }, [justSelected]);
    useEffect(() => { officersRef.current = officers; }, [officers]);
    useEffect(() => { savedEditingFieldForTabRef.current = savedEditingFieldForTab; }, [savedEditingFieldForTab]);

    // Stable labels
    const officerLabel = useMemo(() => getOfficerLabel(), []);
    const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);
    const resolvedTitle = title ?? ('Responding ' + officerPluralLabel);

    const timeFields: TimeFieldInfo[] = useMemo(() => [
        { label: 'Dispatched', field: 'dispatched' },
        { label: 'Enroute', field: 'enroute' },
        { label: 'On Scene', field: 'onScene' },
        { label: 'Cleared', field: 'cleared' },
    ], []);

    const datePickerPopupClass = useMemo(() => theme || '', [theme]);

    // Helpers
    const emitReplace = useCallback((newArr: Officer[]) => {
        onChange?.(newArr);
    }, [onChange]);

    const mapOfficers = useCallback((fn: (o: Officer) => Officer) => {
        emitReplace(officersRef.current.map(fn));
    }, [emitReplace]);

    // Mobile check
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const checkIfMobile = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
        };
        setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', checkIfMobile);
        return () => {
            window.removeEventListener('resize', checkIfMobile);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // Ensure a primary officer on mount
    useEffect(() => {
        if (officers.length > 0 && !officers.some((o) => o.isPrimary)) {
            const next = officers.slice();
            if (next.length > 0) {
                next[0] = { ...next[0], isPrimary: true };
                onChange?.(next);
            }
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // After editingField renders, perform any pending action
    useEffect(() => {
        if (!editingField || !pendingEditActionRef.current) return;
        const action = pendingEditActionRef.current;
        pendingEditActionRef.current = null;

        if (action === 'datePickerDesktop') {
            setDatePickerOpenDesktop(true);
        } else if (action === 'datePickerMobile') {
            setDatePickerOpen(true);
        }
    }, [editingField]);

    // After officers array changes, scroll to last card if requested
    useEffect(() => {
        if (shouldScrollToLastRef.current) {
            shouldScrollToLastRef.current = false;
            if (rootRef.current) scrollToLastMobileCard(rootRef.current);
        }
    }, [officers]);

    // Cleanup close timeout on unmount
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    // Editing helpers
    const isEditing = useCallback((rowUuid: string, field: string) => {
        return editingField?.uuid === rowUuid && editingField?.field === field;
    }, [editingField]);

    const getNextEditableField = useCallback((currentUuid: string, currentField: string): EditingField | null => {
        const editableFields = ['name', 'dispatched', 'enroute', 'onScene', 'cleared'];
        const currentOfficerIndex = officersRef.current.findIndex((o) => o.uuid === currentUuid);
        const currentFieldIndex = editableFields.indexOf(currentField);

        if (currentFieldIndex < editableFields.length - 1) {
            return { uuid: currentUuid, field: editableFields[currentFieldIndex + 1] };
        }

        if (currentOfficerIndex < officersRef.current.length - 1) {
            const nextOfficer = officersRef.current[currentOfficerIndex + 1];
            return { uuid: nextOfficer.uuid, field: editableFields[0] };
        }

        return null;
    }, []);

    const getPreviousEditableField = useCallback((currentUuid: string, currentField: string): EditingField | null => {
        const editableFields = ['name', 'dispatched', 'enroute', 'onScene', 'cleared'];
        const currentOfficerIndex = officersRef.current.findIndex((o) => o.uuid === currentUuid);
        const currentFieldIndex = editableFields.indexOf(currentField);

        if (currentFieldIndex > 0) {
            return { uuid: currentUuid, field: editableFields[currentFieldIndex - 1] };
        }

        if (currentOfficerIndex > 0) {
            const prevOfficer = officersRef.current[currentOfficerIndex - 1];
            return { uuid: prevOfficer.uuid, field: editableFields[editableFields.length - 1] };
        }

        return null;
    }, []);

    const closeEditMode = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setEditingField(null);
        setEditValue('');
        setDatePickerOpen(false);
        setDatePickerOpenDesktop(false);
    }, []);

    const saveEdit = useCallback(() => {
        const ef = editingFieldRef.current;
        if (!ef) return;

        const { uuid, field } = ef;
        const currentEditValue = editValueRef.current;

        if (field === 'name') {
            const selectedPerson = people.find((p) => p.id === currentEditValue);
            emitReplace(officersRef.current.map((o) =>
                o.uuid === uuid
                    ? { ...o, officerId: currentEditValue, name: selectedPerson?.name || `New ${officerLabel}` }
                    : o
            ));
        } else {
            emitReplace(officersRef.current.map((o) =>
                o.uuid === uuid ? { ...o, [field]: currentEditValue } : o
            ));
        }
    }, [people, officerLabel, emitReplace]);

    const cancelEdit = useCallback(() => {
        closeEditMode();
        setSavedEditingFieldForTab(null);
    }, [closeEditMode]);

    const startEdit = useCallback((rowUuid: string, field: string, currentValue: any) => {
        // If already editing something else, save and close it first
        if (editingFieldRef.current) {
            saveEdit();
            closeEditMode();
        }

        setEditingField({ uuid: rowUuid, field });

        if (field === 'name') {
            const officer = officersRef.current.find((o) => o.uuid === rowUuid);
            setEditValue(officer?.officerId ?? null);
        } else {
            setEditValue(currentValue !== null ? currentValue : new Date());
            pendingEditActionRef.current = 'datePickerDesktop';
        }
    }, [saveEdit, closeEditMode]);

    const startEditMobile = useCallback((rowUuid: string, field: string, currentValue: any) => {
        // If already editing something else, save and close it first
        if (editingFieldRef.current) {
            saveEdit();
            closeEditMode();
        }

        setEditingField({ uuid: rowUuid, field });

        if (field === 'name') {
            const officer = officersRef.current.find((o) => o.uuid === rowUuid);
            setEditValue(officer?.officerId ?? null);
        } else {
            setEditValue(currentValue ?? new Date());
            pendingEditActionRef.current = 'datePickerMobile';
        }
    }, [saveEdit, closeEditMode]);

    const clearDate = useCallback(() => {
        setEditValue(null);
        // Save with null then close
        setTimeout(() => {
            const ef = editingFieldRef.current;
            if (!ef) return;
            const { uuid, field } = ef;
            emitReplace(officersRef.current.map((o) =>
                o.uuid === uuid ? { ...o, [field]: null } : o
            ));
            closeEditMode();
        }, 0);
    }, [emitReplace, closeEditMode]);

    const handleDateChange = useCallback((v: Date | null | string) => {
        const newVal = (v === null || v === undefined || v === '') ? null : v;
        editValueRef.current = newVal;
        setEditValue(newVal);
    }, []);

    const handleTabKey = useCallback((event: React.KeyboardEvent) => {
        if (!editingFieldRef.current) return;

        event.preventDefault();
        event.stopPropagation();

        setIsTabbing(true);
        isTabbingRef.current = true;
        const currentField = { ...editingFieldRef.current };
        const isShiftPressed = event.shiftKey;

        // Save current field
        saveEdit();
        closeEditMode();

        setTimeout(() => {
            const next = isShiftPressed
                ? getPreviousEditableField(currentField.uuid, currentField.field)
                : getNextEditableField(currentField.uuid, currentField.field);

            if (next) {
                const officer = officersRef.current.find((o) => o.uuid === next.uuid);
                if (officer) {
                    startEdit(next.uuid, next.field, officer[next.field]);
                }
            }

            setIsTabbing(false);
            isTabbingRef.current = false;
        }, 0);
    }, [saveEdit, closeEditMode, getNextEditableField, getPreviousEditableField, startEdit]);

    const handleDropdownTab = useCallback((tabEvent: { shiftKey: boolean }) => {
        const saved = savedEditingFieldForTabRef.current;
        if (!saved) return;

        const currentField = { ...saved };
        setSavedEditingFieldForTab(null);

        setTimeout(() => {
            const next = tabEvent.shiftKey
                ? getPreviousEditableField(currentField.uuid, currentField.field)
                : getNextEditableField(currentField.uuid, currentField.field);

            if (next) {
                const officer = officersRef.current.find((o) => o.uuid === next.uuid);
                if (officer) {
                    startEdit(next.uuid, next.field, officer[next.field]);
                }
            }
        }, 0);
    }, [getNextEditableField, getPreviousEditableField, startEdit]);

    const handleOfficerSelect = useCallback((item: any) => {
        const ef = editingFieldRef.current;
        if (ef) {
            setSavedEditingFieldForTab({ ...ef });
        }

        setJustSelected(true);
        justSelectedRef.current = true;
        const selectedOfficerId = item?.id ?? null;
        setEditValue(selectedOfficerId);

        if (selectedOfficerId && ef) {
            const uuid = ef.uuid;
            emitReplace(officersRef.current.map((o) =>
                o.uuid === uuid
                    ? { ...o, officerId: selectedOfficerId, name: item?.name || `New ${officerLabel}` }
                    : o
            ));
            closeEditMode();
        } else {
            saveEdit();
        }
    }, [officerLabel, emitReplace, closeEditMode, saveEdit]);

    const handleOfficerCancel = useCallback(() => {
        setSavedEditingFieldForTab(null);
        cancelEdit();
    }, [cancelEdit]);

    const handleDropdownClose = useCallback(() => {
        setTimeout(() => {
            if (editingFieldRef.current && editingFieldRef.current.field === 'name' && !justSelectedRef.current) {
                cancelEdit();
            }
            setJustSelected(false);
            justSelectedRef.current = false;
            if (!justSelectedRef.current) {
                setSavedEditingFieldForTab(null);
            }
        }, 50);
    }, [cancelEdit]);

    const handleDatePickerClose = useCallback(() => {
        setDatePickerOpenDesktop(false);

        if (isTabbingRef.current) return;

        saveEdit();

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            closeEditMode();
        }, 150);
    }, [saveEdit, closeEditMode]);

    const handleDatePickerCloseMobile = useCallback(() => {
        setDatePickerOpen(false);

        if (isTabbingRef.current) return;

        saveEdit();

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            closeEditMode();
        }, 150);
    }, [saveEdit, closeEditMode]);

    // Add / delete / toggle
    const addOfficer = useCallback(() => {
        const isFirst = officersRef.current.length === 0;
        const hasPrimary = officersRef.current.some((o) => o.isPrimary);
        const newOfficer: Officer = {
            uuid: getInsecureUUID(),
            id: null,
            officerId: null,
            name: `Select ${officerLabel}`,
            isPrimary: isFirst || !hasPrimary,
            dispatched: new Date(),
            enroute: null,
            onScene: null,
            cleared: null,
        };
        emitReplace([...officersRef.current, newOfficer]);
        shouldScrollToLastRef.current = true;
    }, [officerLabel, emitReplace]);

    const openDeleteConfirm = useCallback((uuid: string) => {
        const row = officersRef.current.find((o) => o.uuid === uuid);
        setDeleteConfirm({
            open: true,
            uuid,
            name: row?.name || `this ${officerLabel.toLowerCase()}`,
        });
    }, [officerLabel]);

    const cancelDeleteConfirm = useCallback(() => {
        setDeleteConfirm({ open: false, uuid: null, name: '' });
    }, []);

    const confirmDelete = useCallback(() => {
        const uuid = deleteConfirm.uuid;
        if (!uuid) { cancelDeleteConfirm(); return; }

        const toRemove = officersRef.current.find((o) => o.uuid === uuid);
        const remaining = officersRef.current.filter((o) => o.uuid !== uuid);

        if (toRemove?.isPrimary && remaining.length) {
            remaining[0] = { ...remaining[0], isPrimary: true };
        }

        emitReplace(remaining);
        cancelDeleteConfirm();
    }, [deleteConfirm.uuid, emitReplace, cancelDeleteConfirm]);

    const togglePrimary = useCallback((uuid: string) => {
        const officer = officersRef.current.find((o) => o.uuid === uuid);
        if (officer?.isPrimary && officersRef.current.length === 1) return;
        mapOfficers((o) => ({ ...o, isPrimary: o.uuid === uuid }));
    }, [mapOfficers]);

    // Format helpers
    const formatDate = useCallback((date: any) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }, []);

    const formatTime = useCallback((date: any) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }, []);

    // Render helpers for date cells
    const renderDateCell = (officer: Officer, field: string) => {
        const fieldValue = officer[field];

        if (!isReadOnly && !isMobile && isEditing(officer.uuid, field)) {
            return (
                <div className="edit-datetime">
                    <div className="datetime-edit-controls">
                        <DateTimePicker
                            value={editValue}
                            type="datetime"
                            format="DD MMM YYYY HH:mm:ss"
                            showSecond={true}
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpenDesktop}
                            onChange={handleDateChange}
                            onConfirm={saveEdit}
                            onClear={clearDate}
                            onClose={handleDatePickerClose}
                            onKeyDown={handleTabKey}
                        />
                        <button className="btn-clear-date" onClick={clearDate} title="Clear date/time">
                            <i className="fas fa-times-circle" />
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div
                className={!isReadOnly ? 'editable' : undefined}
                onClick={() => !isReadOnly && startEdit(officer.uuid, field, fieldValue)}
            >
                {fieldValue ? (
                    <>
                        <div className="datetime-display">{formatDate(fieldValue)}</div>
                        <div className="time-display">{formatTime(fieldValue)}</div>
                    </>
                ) : (
                    <div className="empty-cell">&mdash;</div>
                )}
            </div>
        );
    };

    const renderDateCellMobile = (officer: Officer, fieldInfo: TimeFieldInfo) => {
        const fieldValue = officer[fieldInfo.field];

        if (!isReadOnly && isMobile && isEditing(officer.uuid, fieldInfo.field)) {
            return (
                <div className="edit-datetime">
                    <div className="datetime-edit-controls-mobile">
                        <DateTimePicker
                            value={editValue}
                            type="datetime"
                            format="DD MMM YYYY HH:mm:ss"
                            showSecond={true}
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpen}
                            onChange={handleDateChange}
                            onConfirm={saveEdit}
                            onClear={clearDate}
                            onClose={handleDatePickerCloseMobile}
                            inputAttr={{ readOnly: isMobile }}
                            onKeyDown={handleTabKey}
                        />
                        <button className="btn-clear-date-mobile" onClick={clearDate} title="Clear date/time">
                            <i className="fas fa-times-circle" />
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div
                className={`card-field-value${!isReadOnly ? ' editable' : ''}`}
                onClick={() => !isReadOnly && startEditMobile(officer.uuid, fieldInfo.field, fieldValue)}
            >
                {fieldValue ? (
                    <>
                        <div className="datetime-display">{formatDate(fieldValue)}</div>
                        <div className="time-display">{formatTime(fieldValue)}</div>
                    </>
                ) : (
                    <div className="empty-cell">&mdash;</div>
                )}
            </div>
        );
    };

    return (
        <div className={`case-report-officers-assigned${theme ? ' ' + theme : ''}`} ref={rootRef}>
            <div className="header">
                <h1>{resolvedTitle}</h1>
                {!isReadOnly && (
                    <button className="btn-add" onClick={addOfficer}>
                        <i className="fas fa-plus" /> {officerLabel.toUpperCase()}
                    </button>
                )}
            </div>

            {/* Desktop table */}
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>{officerLabel}</th>
                            <th style={{ width: '8%', textAlign: 'center' }}>Primary</th>
                            <th style={{ width: '18%' }}>Dispatched</th>
                            <th style={{ width: '18%' }}>En-Route</th>
                            <th style={{ width: '18%' }}>On Scene</th>
                            <th style={{ width: '18%' }}>Cleared</th>
                            <th style={{ width: '8%' }}>Actions</th>
                        </tr>
                    </thead>
                    {officers.length === 0 ? (
                        <tbody>
                            <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    No {officerPluralLabel.toLowerCase()} assigned
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {officers.map((officer) => (
                                <tr key={officer.uuid}>
                                    {/* Name cell */}
                                    <td className="name-cell">
                                        {!isReadOnly && !isMobile && isEditing(officer.uuid, 'name') ? (
                                            <div className="edit-container">
                                                <Dropdownlist
                                                    value={editValue}
                                                    items={people}
                                                    labelKey="name"
                                                    valueKey="id"
                                                    placeholder={`Select ${officerLabel.toLowerCase()}…`}
                                                    defaultOpen={true}
                                                    onItemChange={handleOfficerSelect}
                                                    onCancel={handleOfficerCancel}
                                                    onTab={handleDropdownTab}
                                                    onClose={handleDropdownClose}
                                                />
                                                <button className="btn-cancel" onClick={cancelEdit}>
                                                    <i className="fas fa-times" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className={`officer-name${!isReadOnly ? ' editable' : ''}`}
                                                onClick={() => !isReadOnly && startEdit(officer.uuid, 'name', officer.name)}
                                            >
                                                {officer.name}
                                            </div>
                                        )}
                                    </td>

                                    {/* Primary toggle */}
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className={`primary-toggle${officer.isPrimary ? ' active' : ''}`}
                                            onClick={() => !isReadOnly && togglePrimary(officer.uuid)}
                                            disabled={isReadOnly}
                                            title="Toggle primary"
                                        />
                                    </td>

                                    {/* Time fields */}
                                    {(['dispatched', 'enroute', 'onScene', 'cleared'] as const).map((field) => (
                                        <td key={field}>
                                            {renderDateCell(officer, field)}
                                        </td>
                                    ))}

                                    {/* Actions */}
                                    <td>
                                        {!isReadOnly && (
                                            <div className="actions">
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => openDeleteConfirm(officer.uuid)}
                                                    title={`Remove ${officerLabel}`}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            {/* Mobile cards */}
            <div className="mobile-cards">
                {officers.length === 0 ? (
                    <div className="empty-state">
                        <p>No {officerPluralLabel.toLowerCase()} assigned</p>
                    </div>
                ) : (
                    officers.map((officer) => (
                        <div key={'mobile-' + officer.uuid} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    {!isReadOnly && isMobile && isEditing(officer.uuid, 'name') ? (
                                        <div className="edit-container mobile-edit">
                                            <Dropdownlist
                                                value={editValue}
                                                items={people}
                                                labelKey="name"
                                                valueKey="id"
                                                placeholder={`Select ${officerLabel.toLowerCase()}…`}
                                                defaultOpen={true}
                                                onItemChange={handleOfficerSelect}
                                                onCancel={handleOfficerCancel}
                                                onTab={handleDropdownTab}
                                                onClose={handleDropdownClose}
                                            />
                                            <button className="btn-cancel" onClick={cancelEdit}>
                                                <i className="fas fa-times" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div
                                                className={`card-officer-name${!isReadOnly ? ' editable' : ''}`}
                                                onClick={() => !isReadOnly && startEditMobile(officer.uuid, 'name', officer.name)}
                                            >
                                                {officer.name}
                                                {!isReadOnly && <i className="fa-solid fa-pen-to-square edit-icon" />}
                                            </div>
                                            <button
                                                className={`primary-badge${officer.isPrimary ? ' active' : ''}`}
                                                disabled={isReadOnly}
                                                onClick={() => !isReadOnly && togglePrimary(officer.uuid)}
                                            >
                                                {officer.isPrimary ? '\u25cf PRI' : 'PRI'}
                                            </button>
                                        </>
                                    )}
                                </div>
                                {!isReadOnly && (
                                    <button
                                        className="btn-delete"
                                        onClick={() => openDeleteConfirm(officer.uuid)}
                                        style={{ color: 'white' }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>

                            <div className="card-grid">
                                {timeFields.map((fieldInfo) => (
                                    <div key={fieldInfo.field} className="card-field">
                                        <div className="card-field-label">{fieldInfo.label}</div>
                                        {renderDateCellMobile(officer, fieldInfo)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Delete confirmation modal */}
            <ConfirmModal
                open={deleteConfirm.open}
                title={`Remove ${deleteConfirm.name}?`}
                confirmText="Remove"
                cancelText="Cancel"
                autoFocus="cancel"
                scrollLock={true}
                theme={theme}
                onConfirm={confirmDelete}
                onCancel={cancelDeleteConfirm}
            />
        </div>
    );
}

(window as any).CaseReportOfficersAssignedReact = CaseReportOfficersAssigned;
